// Interpreta por que uma precisão deu 0% (ou ficou indisponível) e devolve uma
// explicação em linguagem simples, orientada à decisão do advogado/admin.
// Não recalcula nada — só lê os mesmos contadores que já alimentam a fórmula
// em run-test-set.use-case.ts (precisionGlobal, PerClassMetric, PerRuleMetric).

import type { PerClassMetric } from '@sophia/shared-schemas'

export type PrecisionExplanationKind = 'zero' | 'unavailable'

export interface PrecisionExplanation {
  kind: PrecisionExplanationKind
  message: string
}

// Frase objetiva do que a precisão global significa — sempre visível, pra
// qualquer valor (não só 0%). Diz numerador/denominador e quantos erraram.
export function describeGlobalPrecision(input: {
  classified: number
  classifiedCorrect: number
}): string {
  const { classified, classifiedCorrect } = input
  if (classified === 0) {
    return 'Nenhuma linha foi classificada pelas regras nesta base — não há precisão a medir.'
  }
  const errors = classified - classifiedCorrect
  if (errors === 0) {
    return `Todas as ${classified} linhas que as regras classificaram foram pra classe certa.`
  }
  const fpLabel = errors === 1 ? '1 foi falso positivo' : `${errors} foram falsos positivos`
  return `Das ${classified} linhas classificadas pelas regras, ${classifiedCorrect} foram pra classe certa e ${fpLabel}.`
}

// Identifica a "classe testada" da base: a classe cadastrada com mais textos
// esperados. É o foco do benchmark — o número que o usuário quer ver em destaque.
export interface TestedClassHighlight {
  className: string
  total: number
  hits: number
  accuracy: number
}

export function pickTestedClass(perClass: PerClassMetric[]): TestedClassHighlight | null {
  const candidates = perClass.filter((c) => c.classId !== null && c.total > 0)
  if (candidates.length === 0) return null
  const dominant = candidates.reduce((a, b) => (b.total > a.total ? b : a))
  return {
    className: dominant.className,
    total: dominant.total,
    hits: dominant.correctlyPredicted,
    accuracy: dominant.total > 0 ? dominant.correctlyPredicted / dominant.total : 0,
  }
}

// Cobertura a partir da qual "0% de precisão" passa de "regra não encontrou nada"
// pra "regra está classificando errado" — o cenário que mais confunde o usuário.
const HIGH_COVERAGE_THRESHOLD = 0.5

export function explainGlobalPrecision(input: {
  classified: number
  classifiedCorrect: number
  totalRows: number
}): PrecisionExplanation | null {
  const { classified, classifiedCorrect, totalRows } = input

  if (classified === 0) {
    return {
      kind: 'unavailable',
      message: 'Precisão indisponível porque nenhuma linha da base foi classificada pelas regras (cobertura 0%).',
    }
  }
  if (classifiedCorrect > 0) return null

  const coverage = totalRows > 0 ? classified / totalRows : 0
  if (coverage >= HIGH_COVERAGE_THRESHOLD) {
    return {
      kind: 'zero',
      message:
        'Precisão 0% com cobertura alta indica que as regras estão classificando os textos, só que sempre na classe errada. Revise pra qual classe os padrões estão apontando.',
    }
  }
  return {
    kind: 'zero',
    message: 'Precisão 0% porque todos os matches encontrados foram falsos positivos.',
  }
}

export function explainClassPrecision(input: {
  total: number
  predictedCount: number
  correctlyPredicted: number
}): PrecisionExplanation | null {
  const { total, predictedCount, correctlyPredicted } = input

  if (total === 0) {
    return {
      kind: 'unavailable',
      message: 'Precisão indisponível porque não há positivos esperados na base — esta classe não aparece na planilha de teste.',
    }
  }
  if (correctlyPredicted > 0) return null

  if (predictedCount === 0) {
    return {
      kind: 'zero',
      message:
        'Precisão 0% porque nenhuma publicação da classe esperada foi corretamente identificada — as regras nunca apontaram essa classe nesta base.',
    }
  }
  return {
    kind: 'zero',
    message: 'Precisão 0% porque todos os matches encontrados foram falsos positivos.',
  }
}

export function explainRulePrecision(input: {
  matches: number
  correctMatches: number
}): PrecisionExplanation | null {
  const { matches, correctMatches } = input

  if (matches === 0) {
    return {
      kind: 'unavailable',
      message: 'Precisão indisponível porque esta regra não encontrou nenhum match nesta base.',
    }
  }
  if (correctMatches > 0) return null

  return {
    kind: 'zero',
    message: 'Precisão 0% porque todos os matches encontrados foram falsos positivos — o padrão dispara, mas sempre na classe errada.',
  }
}
